import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Send, CheckCircle2, ArrowRight, CalendarDays } from "lucide-react";
import { api } from "@/lib/api";
import { POSITIONS } from "@/data/positions";

const inputCls = "w-full bg-transparent border border-slate-200 focus:border-primary-brand outline-none px-4 py-3 text-sm text-slate-900";

const initialForm = {
  full_name: "", email: "", phone: "", city: "", state: "UT",
  position: POSITIONS[0].key,
  years_experience: "", cdl_class: "", endorsements: "",
  availability: "full_time", earliest_start: "",
  previous_employer: "", experience_summary: "",
  resume_url: "", referral_source: "",
  consent: true,
};

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-2">{label}</div>
      {children}
    </label>
  );
}

function SectionTitle({ children }) {
  return <div className="text-xs uppercase tracking-[0.25em] font-bold text-primary-brand">{children}</div>;
}

export function ApplicationForm({ presetPosition }) {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (presetPosition) setForm((f) => ({ ...f, position: presetPosition }));
  }, [presetPosition]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone) {
      toast.error("Please fill your name, email, and phone.");
      return;
    }
    if (!form.consent) {
      toast.error("Please confirm your information is accurate before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/careers/apply", form);
      setSubmitted(true);
      toast.success("Application received. We'll be in touch soon!");
      setForm(initialForm);
    } catch (err) {
      console.error("Application submit failed:", err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Submit failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedPos = POSITIONS.find((p) => p.key === form.position);
  const isDriver = form.position?.startsWith("truck_driver");

  if (submitted) {
    return (
      <div data-testid="careers-success" className="mt-10 border border-slate-200 bg-white p-10 lg:p-14 text-center">
        <CheckCircle2 className="h-14 w-14 text-primary-brand mx-auto" />
        <h3 className="font-heading text-3xl font-black mt-5 text-slate-900">Application Received</h3>
        <p className="text-slate-600 mt-3 max-w-md mx-auto">
          Thanks for applying to Laronta Pro. Our Operations Director will review your info
          and reach out within a few business days.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/book"
            data-testid="careers-book-interview"
            className="inline-flex items-center gap-2 bg-primary-brand hover:bg-[#E04F00] text-white px-6 py-3 uppercase tracking-wider text-sm font-bold"
          >
            <CalendarDays className="h-4 w-4" /> Book an interview time
          </Link>
          <button
            onClick={() => setSubmitted(false)}
            data-testid="careers-submit-another"
            className="inline-flex items-center gap-2 border border-primary-brand text-primary-brand px-6 py-3 uppercase tracking-wider text-sm font-bold hover:bg-primary-brand hover:text-white transition-colors"
          >
            Submit another <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="careers-form"
      className="mt-10 border border-slate-200 bg-white p-6 lg:p-10 space-y-8"
    >
      <div>
        <SectionTitle>Applying For</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <Field label="Position *">
            <select value={form.position} onChange={upd("position")} className={inputCls} data-testid="app-position" required>
              {POSITIONS.map((p) => <option key={p.key} value={p.key}>{p.title}</option>)}
            </select>
          </Field>
          <Field label="Availability">
            <select value={form.availability} onChange={upd("availability")} className={inputCls} data-testid="app-availability">
              <option value="full_time">Full-time</option>
              <option value="part_time">Part-time</option>
              <option value="contract">Contract</option>
            </select>
          </Field>
        </div>
        {presetPosition && selectedPos && (
          <div className="mt-4 text-xs text-slate-600 border border-slate-200 bg-slate-50 p-3">
            Applying for <span className="font-bold text-slate-900">{selectedPos.title}</span> · {selectedPos.location}{selectedPos.payRange ? ` · ${selectedPos.payRange}` : ""}
          </div>
        )}
      </div>

      <div>
        <SectionTitle>About You</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <Field label="Full name *"><input required value={form.full_name} onChange={upd("full_name")} data-testid="app-name" className={inputCls} /></Field>
          <Field label="Email *"><input type="email" required value={form.email} onChange={upd("email")} data-testid="app-email" className={inputCls} /></Field>
          <Field label="Phone *"><input required value={form.phone} onChange={upd("phone")} data-testid="app-phone" className={inputCls} /></Field>
          <Field label="Earliest start date"><input type="date" value={form.earliest_start} onChange={upd("earliest_start")} data-testid="app-start" className={inputCls} /></Field>
          <Field label="City"><input value={form.city} onChange={upd("city")} data-testid="app-city" className={inputCls} /></Field>
          <Field label="State"><input value={form.state} onChange={upd("state")} data-testid="app-state" className={inputCls} /></Field>
        </div>
      </div>

      <div>
        <SectionTitle>Experience</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <Field label="Years of experience">
            <select value={form.years_experience} onChange={upd("years_experience")} className={inputCls} data-testid="app-years">
              <option value="">Select…</option>
              <option value="0-1">Less than 1 year</option>
              <option value="1-3">1 – 3 years</option>
              <option value="3-5">3 – 5 years</option>
              <option value="5-10">5 – 10 years</option>
              <option value="10+">10+ years</option>
            </select>
          </Field>
          <Field label="Previous employer"><input value={form.previous_employer} onChange={upd("previous_employer")} data-testid="app-employer" className={inputCls} /></Field>

          {isDriver && (
            <>
              <Field label="CDL class">
                <select value={form.cdl_class} onChange={upd("cdl_class")} className={inputCls} data-testid="app-cdl">
                  <option value="">Select…</option>
                  <option value="A">Class A</option>
                  <option value="B">Class B</option>
                  <option value="C">Class C</option>
                </select>
              </Field>
              <Field label="Endorsements">
                <input value={form.endorsements} onChange={upd("endorsements")} placeholder="e.g. H, N, T" className={inputCls} data-testid="app-endorse" />
              </Field>
            </>
          )}

          <Field label="Experience summary" full>
            <textarea rows={4} value={form.experience_summary} onChange={upd("experience_summary")} placeholder="Briefly describe your relevant experience, routes, equipment, or skills." className={inputCls + " resize-none"} data-testid="app-summary" />
          </Field>
          <Field label="Resume link (Google Drive / Dropbox / LinkedIn)" full>
            <input value={form.resume_url} onChange={upd("resume_url")} placeholder="https://" className={inputCls} data-testid="app-resume" />
          </Field>
          <Field label="How did you hear about us?" full>
            <input value={form.referral_source} onChange={upd("referral_source")} placeholder="Indeed, Google, referral, driver…" className={inputCls} data-testid="app-referral" />
          </Field>
        </div>
      </div>

      <label className="flex items-start gap-3 text-sm text-slate-600">
        <input type="checkbox" checked={form.consent} onChange={upd("consent")} data-testid="app-consent" className="mt-1 accent-orange-600" />
        <span>
          I confirm my information is accurate and I understand Laronta Pro Logistic LLC may
          contact me about this application.
        </span>
      </label>

      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-slate-100">
        <div className="text-xs text-slate-500">
          Reviewed by the Operations Director — usually within 2 business days.
        </div>
        <button
          type="submit"
          disabled={submitting}
          data-testid="careers-submit-btn"
          className="inline-flex items-center gap-2 bg-primary-brand hover:bg-[#E04F00] disabled:opacity-60 text-white px-8 py-4 font-bold uppercase tracking-wider text-sm"
        >
          {submitting ? "Submitting…" : "Submit Application"} <Send className="h-4 w-4" />
        </button>
      </div>
    </form>
  );
}
